import { useState, useRef, KeyboardEvent, forwardRef, useImperativeHandle, useEffect } from 'react'
import { cn } from '@/utils/cn'
import { Send, Mic, MicOff, Paperclip, Camera, X, Image as ImageIcon, FileText, Square } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { Camera as CapacitorCamera, CameraSource, CameraResultType, CameraDirection } from '@capacitor/camera'

export interface ChatInputHandle {
  focus: () => void
  setValue: (text: string) => void
}

interface ChatInputProps {
  onSend: (message: string, image?: File, audioBlob?: Blob, documents?: File[]) => void
  onStop?: () => void
  isGenerating?: boolean
  disabled?: boolean
  placeholder?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({
  onSend,
  onStop,
  isGenerating = false,
  disabled = false,
  placeholder = "Mensagem"
}, ref) => {
  const [message, setMessage] = useState('')
  const [pendingImage, setPendingImage] = useState<File | null>(null)
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null)
  const [pendingDocuments, setPendingDocuments] = useState<File[]>([])
  const [isRecording, setIsRecording] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const attachMenuRef = useRef<HTMLDivElement>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setShowAttachMenu(false)
      }
    }
    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showAttachMenu])

  // Auto-resize textarea quando message muda (inclusive via setValue programático)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 100)}px`
    }
  }, [message])

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    setValue: (text: string) => {
      setMessage(text)
      setTimeout(() => textareaRef.current?.focus(), 0)
    },
  }), [])

  const clearPendingImage = () => {
    if (pendingImageUrl) URL.revokeObjectURL(pendingImageUrl)
    setPendingImage(null)
    setPendingImageUrl(null)
  }

  const clearPendingDocuments = () => {
    setPendingDocuments([])
  }

  const removeDocument = (index: number) => {
    setPendingDocuments(prev => prev.filter((_, i) => i !== index))
  }

  const isNative = Capacitor.isNativePlatform()

  const handleCamera = async () => {
    try {
      const photo = await CapacitorCamera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        quality: 90,
        allowEditing: false,
        direction: CameraDirection.Rear,
      })
      const response = await fetch(photo.webPath!)
      const blob = await response.blob()
      const file = new File([blob], `camera-${Date.now()}.${photo.format}`, {
        type: `image/${photo.format}`,
      })
      clearPendingImage()
      setPendingImage(file)
      setPendingImageUrl(URL.createObjectURL(file))
    } catch (err) {
      console.error('[ChatInput] Camera error:', err)
    }
  }

  const handleSend = () => {
    if ((!message.trim() && !pendingImage && pendingDocuments.length === 0) || disabled || isRecording) return
    onSend(message.trim(), pendingImage ?? undefined, undefined, pendingDocuments.length > 0 ? pendingDocuments : undefined)
    setMessage('')
    clearPendingImage()
    clearPendingDocuments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      alert('Apenas imagens são suportadas. Por favor, selecione um arquivo de imagem (JPG, PNG, WEBP, etc.).')
      e.target.value = ''
      return
    }
    clearPendingImage()
    setPendingImage(file)
    setPendingImageUrl(URL.createObjectURL(file))
    e.target.value = ''
  }

  const handleDocumentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    const allowedExtensions = ['.pdf', '.docx', '.txt', '.md']
    const validFiles: File[] = []
    for (const file of files) {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
      if (!allowedExtensions.includes(ext)) {
        alert(`Formato não suportado: ${file.name}. Formatos aceitos: PDF, DOCX, TXT, MD.`)
        continue
      }
      if (file.size > 30 * 1024 * 1024) {
        alert(`O documento ${file.name} excede o limite de 30 MB.`)
        continue
      }
      validFiles.push(file)
    }
    const combined = [...pendingDocuments, ...validFiles]
    if (combined.length > 5) {
      alert(`Limite de 5 documentos atingido. Apenas os primeiros 5 foram adicionados.`)
    }
    setPendingDocuments(combined.slice(0, 5))
    e.target.value = ''
  }

  const handleMicToggle = async () => {
    if (disabled) return

    if (isRecording) {
      mediaRecorderRef.current?.stop()
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Pick the best audio-only format supported by this browser
      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ]
      const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t))
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const actualMime = mediaRecorder.mimeType || 'audio/webm'
        // Map to OpenAI-compatible extension
        const ext = actualMime.includes('ogg') ? 'ogg'
          : actualMime.includes('mp4') ? 'mp4'
            : 'webm'
        const audioFile = new File(audioChunksRef.current, `audio.${ext}`, { type: actualMime })
        audioChunksRef.current = []
        setIsRecording(false)
        onSend('', undefined, audioFile)
      }

      mediaRecorder.start()
      mediaRecorderRef.current = mediaRecorder
      setIsRecording(true)
    } catch (err) {
      console.error('[ChatInput] Mic error:', err)
    }
  }

  const micSupported = typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  const canSend = !!(message.trim() || pendingImage || pendingDocuments.length > 0) && !disabled && !isRecording

  return (
    <div className="flex flex-col gap-2">
      {/* Image preview */}
      {pendingImageUrl && pendingImage && (
        <div className="flex items-center gap-2 px-1">
          <div className="relative inline-block">
            <img
              src={pendingImageUrl}
              alt="Imagem selecionada"
              className="h-16 w-16 object-cover rounded-lg border border-brand-border"
            />
            <button
              onClick={clearPendingImage}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-surface-alt border border-brand-border rounded-full flex items-center justify-center hover:bg-red-100 transition-colors"
            >
              <X size={11} className="text-brand-text-muted" />
            </button>
          </div>
          <span className="text-xs text-brand-text-muted truncate max-w-[200px]">
            {pendingImage.name}
          </span>
        </div>
      )}

      {/* Document preview */}
      {pendingDocuments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {pendingDocuments.map((doc, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="relative inline-block">
                <div className="h-16 w-16 bg-brand-surface-alt rounded-lg border border-brand-border flex items-center justify-center">
                  <FileText size={24} className="text-brand-accent" />
                </div>
                <button
                  onClick={() => removeDocument(idx)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-surface-alt border border-brand-border rounded-full flex items-center justify-center hover:bg-red-100 transition-colors"
                >
                  <X size={11} className="text-brand-text-muted" />
                </button>
              </div>
              <span className="text-xs text-brand-text-muted truncate max-w-[200px]">
                {doc.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="flex items-center gap-2 px-1">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-red-500 font-medium">
            Gravando... Toque no microfone para enviar
          </span>
        </div>
      )}

      {/* Main input row - WhatsApp style */}
      <div className="flex gap-2 items-end px-1">
        {/* Mic button - always on the left */}
        <button
          onClick={handleMicToggle}
          disabled={disabled || !micSupported}
          title={!micSupported ? 'Gravação não suportada' : isRecording ? 'Parar gravação' : 'Gravar mensagem de voz'}
          className={cn(
            "p-3 rounded-full transition-all duration-200 flex-shrink-0",
            "h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center",
            isRecording
              ? "bg-red-500 text-white animate-pulse"
              : "bg-brand-surface-alt text-brand-text-muted hover:text-brand-text hover:bg-brand-surface border border-brand-border",
            (disabled || !micSupported) && "opacity-50 cursor-not-allowed"
          )}
        >
          {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
        </button>

        {/* Input container with integrated attach menu */}
        <div className="flex-1 relative min-w-0">
          <div className="flex items-end gap-1.5 sm:gap-2 bg-brand-input-bg border border-brand-input-border rounded-2xl px-2 sm:px-3 py-2 focus-within:ring-2 focus-within:ring-brand-accent focus-within:border-transparent transition-all">
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={isRecording ? 'Gravando áudio...' : placeholder}
              disabled={disabled || isRecording}
              className={cn(
                "flex-1 resize-none bg-transparent border-0 text-brand-text min-w-0",
                "placeholder:text-brand-text-muted",
                "focus:outline-none focus:ring-0",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "min-h-[28px] sm:min-h-[32px] max-h-[100px]",
                "py-1 leading-5 text-sm sm:text-base"
              )}
              rows={1}
            />

            {/* Attach button with menu */}
            <div ref={attachMenuRef} className="relative flex-shrink-0">
              <button
                onClick={() => !disabled && !isRecording && setShowAttachMenu(!showAttachMenu)}
                disabled={disabled || isRecording}
                className={cn(
                  "p-1.5 sm:p-2 rounded-full transition-all duration-200 flex-shrink-0",
                  "flex items-center justify-center",
                  pendingImage || showAttachMenu
                    ? "text-brand-accent"
                    : "text-brand-text-muted hover:text-brand-text",
                  (disabled || isRecording) && "opacity-50 cursor-not-allowed"
                )}
              >
                <Paperclip size={20} className="sm:w-[22px] sm:h-[22px]" />
              </button>

              {/* Attachment menu popup */}
              {showAttachMenu && (
                <div className={cn(
                  "absolute z-50 bg-white dark:bg-brand-surface border border-brand-border rounded-xl shadow-lg overflow-hidden",
                  "bottom-full right-0 mb-2 min-w-[160px]"
                )}>
                  <button
                    onClick={() => {
                      if (isNative) {
                        handleCamera()
                      } else {
                        cameraInputRef.current?.click()
                      }
                      setShowAttachMenu(false)
                    }}
                    disabled={disabled || isRecording}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-brand-text hover:bg-brand-surface-alt transition-colors"
                  >
                    <Camera size={18} className="text-brand-accent" />
                    <span>Câmera</span>
                  </button>
                  <div className="h-px bg-brand-border mx-3" />
                  <button
                    onClick={() => {
                      fileInputRef.current?.click()
                      setShowAttachMenu(false)
                    }}
                    disabled={disabled || isRecording}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-brand-text hover:bg-brand-surface-alt transition-colors"
                  >
                    <ImageIcon size={18} className="text-brand-accent" />
                    <span>Galeria</span>
                  </button>
                  <div className="h-px bg-brand-border mx-3" />
                  <button
                    onClick={() => {
                      documentInputRef.current?.click()
                      setShowAttachMenu(false)
                    }}
                    disabled={disabled || isRecording}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-brand-text hover:bg-brand-surface-alt transition-colors"
                  >
                    <FileText size={18} className="text-brand-accent" />
                    <span>Documento</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stop button — visível durante geração */}
        {isGenerating && (
          <button
            onClick={onStop}
            title="Parar geração"
            className="p-3 rounded-full font-medium transition-all duration-200 flex-shrink-0 h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center bg-brand-primary text-white dark:text-verde-500 hover:bg-brand-primary-hover"
          >
            <Square size={16} fill="currentColor" />
          </button>
        )}

        {/* Send button — visível quando há conteúdo e não está gerando */}
        {!isGenerating && <button
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            "p-3 rounded-full font-medium transition-all duration-200 flex-shrink-0",
            canSend
              ? "bg-brand-primary text-white dark:text-verde-500 hover:bg-brand-primary-hover scale-100"
              : "bg-brand-surface-alt text-brand-text-muted scale-90 opacity-0 pointer-events-none",
            "h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center"
          )}
        >
          <Send size={20} />
        </button>}

        {/* Hidden file input - gallery */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        {/* Hidden file input - camera capture */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
        {/* Hidden file input - documents */}
        <input
          ref={documentInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
          onChange={handleDocumentSelect}
          className="hidden"
        />
      </div>
    </div>
  )
})

ChatInput.displayName = 'ChatInput'